import * as React from 'react';
import classnames from 'classnames';
import {
  CopyOutlined,
  DeleteOutlined,
  LeftOutlined,
  PasteSquareOutlined,
  RightOutlined,
} from '@easyv/react-icons';
import { Dropdown, Input, Menu } from '@easyv/react-components';
import { ReactSortable } from 'react-sortablejs';
import { Component } from '@easytwin/runtime';
import { isNil } from '@/utils';
import { DropListMenuOption, getDropListMenu } from '../../UIGenerator/uicomponents/DropListMenu';
import { observer } from 'mobx-react-lite';
import { FunctionalCompTab, HoverState } from './FunctionalCompTab';

export interface FunctionalCompTabsProp {
  dataSource: Component[];
  selectKey?: string;
  disablePaste?: boolean;
  allowEmpty?: boolean;
  renamingStateId: string | undefined;
  onTabChange?: (key: string) => void;
  onDeleteState?: (key: string) => void;
  onCopyState?: (key: string) => void;
  onPasteState?: (key: string) => void;
  onRenameStateStart: (id: string) => void;
  onRenameStateEnd: (name: string) => void;
  onSortState: (originId: string, toId: string, state: HoverState) => void;
}

export const FunctionalCompTabs = observer(
  ({
    dataSource,
    selectKey,
    renamingStateId,
    allowEmpty = false,
    onTabChange,
    onDeleteState,
    onCopyState,
    onPasteState,
    onRenameStateStart,
    onRenameStateEnd,
    onSortState,
  }: FunctionalCompTabsProp) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const listRef = React.useRef<(HTMLElement | null)[]>([]);
    const [leftDropList, setLeftDropList] = React.useState<React.ReactNode>();
    const [rightDropList, setRightDropList] = React.useState<React.ReactNode>();

    const handleScroll = React.useCallback(
      (stateId: string) => {
        if (!scrollRef.current) return;
        const i = dataSource.findIndex((a) => a.id === stateId);
        if (i === -1) return;

        const itemRef = listRef.current[i];
        if (isNil(itemRef)) return;
        if (!itemRef) return;
        /** 没有在视野外，不滚动 */
        if (
          itemRef.offsetLeft > scrollRef.current.scrollLeft &&
          itemRef.offsetLeft + itemRef.offsetWidth <
            scrollRef.current.scrollLeft + scrollRef.current.offsetWidth
        )
          return;

        scrollRef.current.scrollTo({
          left: itemRef.offsetLeft,
          behavior: 'smooth',
        });
      },
      [dataSource],
    );

    const switchToPrev = () => {
      const i = dataSource.findIndex((a) => a.id === selectKey);
      if (i === -1) return;
      if (i - 1 >= 0) {
        onTabChange?.(dataSource[i - 1].id);
      }
    };

    const switchToNext = () => {
      const i = dataSource.findIndex((a) => a.id === selectKey);
      if (i === -1) return;
      if (i + 1 < dataSource.length) {
        onTabChange?.(dataSource[i + 1].id);
      }
    };

    React.useEffect(() => {
      selectKey && handleScroll(selectKey);
      // 避免比如修改状态时也会导致触发这里的逻辑
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectKey]);

    const droplist: DropListMenuOption[] = [
      {
        key: '1',
        label: '复制动画',
        icon: <CopyOutlined />,
        onSelected: () => {
          selectKey && onCopyState?.(selectKey);
        },
      },
      {
        key: '2',
        label: '粘贴动画',
        icon: <PasteSquareOutlined />,
        onSelected: () => {
          selectKey && onPasteState?.(selectKey);
        },
      },
      {
        key: '3',
        label: '删除',
        icon: <DeleteOutlined />,
        visible: allowEmpty ? dataSource.length > 0 : dataSource.length > 1,
        onSelected: () => {
          selectKey && onDeleteState?.(selectKey);
        },
      },
    ];

    React.useEffect(() => {
      const calcHiddenItems = () => {
        const leftHiddenItems = dataSource.filter((_, index) => {
          const node = listRef.current[index];
          if (!node || !scrollRef.current) return false;
          return node.offsetLeft < scrollRef.current.scrollLeft;
        });

        const rightHiddenItems = dataSource.filter((_, index) => {
          const node = listRef.current[index];
          if (!node || !scrollRef.current) return false;
          return (
            node.offsetLeft + node.clientWidth >
            scrollRef.current.scrollLeft + scrollRef.current.clientWidth
          );
        });

        const leftDropList = (
          <Menu>
            {leftHiddenItems.map((item) => {
              return (
                <Menu.Item
                  key={item.id}
                  onClick={() => {
                    onTabChange?.(item.id);
                  }}
                >
                  <div className='w-full text-center'>{item.name}</div>
                </Menu.Item>
              );
            })}
          </Menu>
        );

        const rightDropList = (
          <Menu>
            {rightHiddenItems.map((item) => {
              return (
                <Menu.Item
                  key={item.id}
                  onClick={() => {
                    onTabChange?.(item.id);
                  }}
                >
                  <div className='w-full text-center'>{item.name}</div>
                </Menu.Item>
              );
            })}
          </Menu>
        );

        setLeftDropList(leftDropList);
        setRightDropList(rightDropList);
      };

      calcHiddenItems();
      const scrollerNode = scrollRef.current;
      scrollerNode?.addEventListener('scroll', calcHiddenItems);

      return () => {
        scrollerNode?.removeEventListener('scroll', calcHiddenItems);
      };
    }, [dataSource, handleScroll, onTabChange]);

    return (
      <>
        <div className='flex rounded-sm h-9 items-center'>
          <Dropdown trigger='hover' position='bottom' droplist={leftDropList}>
            <div
              className='cursor-pointer  flex justify-center items-center hover:bg-bgColorHover rounded-[10px] w-5 h-5 bg-FFFFFF0A'
              onClick={switchToPrev}
            >
              <LeftOutlined size={12} />
            </div>
          </Dropdown>
          <div ref={scrollRef} className='flex-1 overflow-x-auto no-scroll-container relative'>
            <div className='flex px-[6px]'>
              {dataSource.map((item, index) => (
                <FunctionalCompTab
                  key={item.id}
                  ref={(node) => {
                    listRef.current[index] = node;
                  }}
                  item={item}
                  droplist={droplist}
                  selectedStateId={selectKey}
                  renamingStateId={renamingStateId}
                  onTabChange={onTabChange}
                  onRenameStateStart={onRenameStateStart}
                  onRenameStateEnd={onRenameStateEnd}
                  onSort={onSortState}
                />
              ))}
            </div>
          </div>
          <Dropdown trigger='hover' position='bottom' droplist={rightDropList}>
            <div
              className='cursor-pointer  flex justify-center items-center hover:bg-bgColorHover rounded-[10px] w-5 h-5 bg-FFFFFF0A'
              onClick={switchToNext}
            >
              <RightOutlined size={12} />
            </div>
          </Dropdown>
        </div>
      </>
    );
  },
);
